import React from "react";
import {
  DatePicker,
  DayOfWeek,
  DefaultButton,
  DetailsList,
  DetailsListLayoutMode,
  DetailsRow,
  Dropdown,
  Fabric,
  IBasePickerSuggestionsProps,
  IColumn,
  IDetailsListProps,
  IDetailsRowStyles,
  IDropdownOption,
  IPersonaProps,
  ISuggestionItemProps,
  ITag,
  Label,
  NormalPeoplePicker,
  PanelType,
  PeoplePickerItemSuggestion,
  PrimaryButton,
  SelectionMode,
  Stack,
  TagPicker,
  Text,
  TextField,
  Toggle,
  TooltipHost
} from "@fluentui/react";
import { FieldArray, Formik } from "formik";
import {
  addPreviousMeeting,
  getAllCustomers,
  updatePreviousMeeting,
  getPreviousMeetingParticipants,
  exportPreviousMeetingPDF
} from "../../../services/customers/CustomerService";
import {
  ICustomers,
  IPreviousMeeting,
  IPreviousMeetingActionItems,
  IPreviousMeetingActionItemsSearch,
} from "../../../vm";
import { childrenGap, childrenGap8, MEETINGS_LEGEND_DICT, MEETINGS_LOCATION_DICT, MEETINGS_OWNER_DICT, MEETINGS_STATUS_DICT, MEETINGS_STATUS_DROPDOWN } from "../../common/Constants";
import CustomDrawer from "../../common/CustomDrawer";
import { CustomTextField } from "../../common/Library";
import { ToastContext } from "../../common/ToastProvider";
import * as yup from "yup";
import { parseJwt } from "../../../services/UtilService";
import { getCemAssignToACustomer } from "../../../services/datacenter/DataCenterManagerService";
import Datetime from "react-datetime";
import moment from "moment";
import { mdiTrashCan, mdiArrowUp, mdiArrowDown } from "@mdi/js";
import Icon from "@mdi/react";
import { Loading } from "../../../services/Loading";
import ManagePmSendEmailDialog from "./ManagePmSendEmailDialog";
import { getMomStatusDetailsExcel } from "../../../services/dashboard/DashboardService";
import { ConfirmDialogContext } from "../../common/ConfirmDialogProvider";

const pMSchema = yup.object().shape({
  customerId: yup.string().required("required"),
  meetingDate: yup.date().required("required"),
  agenda: yup.string().required("required"),
  meetingLocation: yup.string().required("required"),
  userId: yup.string().required("required"),
  sttParticipantName: yup.string().required("required"),
  sttParticipantEmail: yup.string().required("required"),
  customerParticipantName: yup.string().required("required"),
  customerParticipantEmail: yup.string().required("required"),
  actionItems: yup.array().of(
    yup.object().shape({
      discussion: yup.string().required("required"),
      legendId: yup.string().required("required"),
      owner: yup.string().required("required"),
      ownerName: yup.string().required("required"),
      ownerEmail: yup.string().required("required"),
      startDate: yup.date().required("required"),
      targetDate: yup.date().required("required"),
      // completionDate: yup.date().optional(),
      completionDate: yup.date().when("status", {
        is: "closed",
        then: yup.date().required("required"),
        else: yup.date(),
      }),
      remarks: yup.string().required("required"),
      status: yup.string().required("required"),
    })
  )
});

export interface ManagePmDialogProps {
  data?: IPreviousMeeting;
  handleLoading: any;
  handleDialogClose: any;
  customerId: string;
  userList: IDropdownOption[];
  isFromDashboard?: boolean;
  customerName?: string;
  isStepTwoView?: boolean;
}

const ManagePmDialog: React.FC<ManagePmDialogProps> = ({
  data,
  handleDialogClose,
  handleLoading,
  customerId,
  userList,
  isFromDashboard,
  customerName,
  isStepTwoView
}) => {
  // token
  let tokenData = parseJwt();
  const isCem = tokenData.role === 2;

  //state
  const [state, setState] = React.useState({
    previousMeeting: {
      customerId: customerId,
      meetingDate: new Date(),
      agenda: "",
      meetingLocation: "",
      userId: data?.userId ? data.userId : isCem ? tokenData._id : "",
      sttParticipantName: "",
      sttParticipantEmail: "",
      customerParticipantName: "",
      customerParticipantEmail: "",
      actionItems: [],
      ...data,
    },
    isLoading: false,
  } as {
    previousMeeting: IPreviousMeeting;
    isLoading: boolean;
  });
  const [selectedCustomers, setSelectedCustomers] = React.useState([] as IPersonaProps[]);
  const [usersList, setUsersList] = React.useState([...userList] as IDropdownOption[]);
  const [step, setStep] = React.useState(1); // 1, 2
  const [isOnEdit, setIsOnEdit] = React.useState(false);
  const [isUpdateAction, setIsUpdateAction] = React.useState(false);
  const [isSendEmailDialogOpen, setIsSendEmailDialogOpen] = React.useState(false as boolean);
  const sttParticipantNamePicker = React.useRef(null as any);
  const sttParticipantEmailPicker = React.useRef(null as any);
  const customerParticipantNamePicker = React.useRef(null as any);
  const customerParticipantEmailPicker = React.useRef(null as any);
  const [ownerNamePickerDict] = React.useState({
    0: React.useRef(null),
    1: React.useRef(null),
    2: React.useRef(null),
    3: React.useRef(null),
    4: React.useRef(null),
    5: React.useRef(null),
    6: React.useRef(null),
    7: React.useRef(null),
    8: React.useRef(null),
    9: React.useRef(null),
    10: React.useRef(null),
  } as any);
  const [ownerEmailPickerDict] = React.useState({
    0: React.useRef(null),
    1: React.useRef(null),
    2: React.useRef(null),
    3: React.useRef(null),
    4: React.useRef(null),
    5: React.useRef(null),
    6: React.useRef(null),
    7: React.useRef(null),
    8: React.useRef(null),
    9: React.useRef(null),
    10: React.useRef(null),
  } as any);
  const [customer, setCustomer] = React.useState(customerName as string);
  const [momActionsSearch, setMomActionsSearch] = React.useState({
    meetingId: data?._id,
    sortBy: "startDate",
    sortOrder: "asc",
    legendId: [],
    status: [],
    owner: [],
    ownerName: [],
  } as IPreviousMeetingActionItemsSearch);

  //hooks
  const { showToast } = React.useContext(ToastContext);
  const { showConfirmDialog } = React.useContext(ConfirmDialogContext);

  React.useEffect(() => {
    if (isStepTwoView) setStep(2);
    if (data?._id && isFromDashboard) {
      setSelectedCustomers([{ id: data._id, text: customerName }]);
    };
    if (data) {
      setIsUpdateAction(true);
      if (data.actionItems && data.actionItems.length > 1) {
        data.actionItems = data.actionItems.sort((a: any, b: any) => momActionsSearch.sortOrder === "asc" ? a[momActionsSearch.sortBy] > b[momActionsSearch.sortBy] ? 1 : -1 : b[momActionsSearch.sortBy] > a[momActionsSearch.sortBy] ? 1 : -1);
        setState({ ...state, previousMeeting: { ...state.previousMeeting, ...data } });
      }
    }
  }, [])

  const closeAddDialog = (data?: IPreviousMeeting) => {
    handleDialogClose(data);
  };

  const pickerSuggestionsProps: IBasePickerSuggestionsProps = {
    noResultsFoundText: "No Customer found",
  };

  const onRenderSuggestionsItem = (personaProps: IPersonaProps, suggestionsProps: ISuggestionItemProps<IPersonaProps>) => (
    <PeoplePickerItemSuggestion
      personaProps={personaProps}
      suggestionsProps={suggestionsProps}
      styles={{ personaWrapper: { width: '100%' } }}
    />
  );

  const onFilterChanged = async (
    filterText: string,
    tagList?: IPersonaProps[]
  ): Promise<IPersonaProps[]> => {
    if (filterText && filterText.length >= 2) {
      let result = await getAllCustomers({
        search: filterText
      });
      if (result?.success) {
        let customers: IPersonaProps[] = [];
        result?.data.forEach((item: ICustomers) => {
          customers.push({
            text: item.name,
            secondaryText: item.regSapNo,
            id: item._id,
          });
        });
        return customers;
      } else {
        return [];
      }
    }
    return [];
  };

  const getCemUser = async (customerId: string) => {
    let result = await getCemAssignToACustomer([customerId]);
    if (result?.success) {
      let data = result.data[customerId] || [];
      const dict: any = {};
      const listArray = data.map((d: any) => {
        return { key: d._id, text: d.name };
      });
      setUsersList(listArray);
    };
  };

  const onParticipantNameEmailSearch = async (
    filterText: string,
    type: string,
    customerId: string,
    selectedTagList?: ITag[],
    setFieldValue?: Function,
    prevData?: any,
    index?: number
  ): Promise<ITag[]> => {
    const isCopiedFromOutlook = filterText.includes("<") && filterText.includes(">");
    const isCopiedFromExcel = filterText.includes(",");
    if (isCopiedFromOutlook || isCopiedFromExcel) {
      const data: string[] = prevData ? prevData.split(",") : [];
      const nameEmailArr = isCopiedFromOutlook ? filterText.split(";") : filterText.split(",");
      nameEmailArr.forEach((d) => {
        if (d.trim()) {
          let arr: string[] = [];
          if (isCopiedFromOutlook) arr = d.split("<");
          if (["sttParticipantName", "customerParticipantName", "ownerName"].includes(type)) {
            let name = isCopiedFromOutlook ? arr[0].replaceAll(`"`, "").trim() : d.trim();
            if (name.includes(",")) name = name.replaceAll(",", "");
            data.push(name);
          } else {
            const email = isCopiedFromOutlook ? arr[1].replaceAll(`>`, "").trim().toLowerCase() : d.trim().toLowerCase();
            data.push(email);
          }
        }
      });

      const set: any = new Set(data);
      if (index !== undefined) {
        if (setFieldValue) setFieldValue(`actionItems[${index}].${type}`, [...set].join(","));
      } else {
        if (setFieldValue) setFieldValue(type, [...set].join(","));
      }

      // to clear the input field
      switch (type) {
        case "sttParticipantName":
          if (sttParticipantNamePicker.current) {
            sttParticipantNamePicker.current.input.current._updateValue("");
          }
          break;
        case "sttParticipantEmail":
          if (sttParticipantEmailPicker.current) {
            sttParticipantEmailPicker.current.input.current._updateValue("");
          }
          break;
        case "customerParticipantName":
          if (customerParticipantNamePicker.current) {
            customerParticipantNamePicker.current.input.current._updateValue("");
          }
          break;
        case "customerParticipantEmail":
          if (customerParticipantEmailPicker.current) {
            customerParticipantEmailPicker.current.input.current._updateValue("");
          }
          break;
        case "ownerName":
          if (index !== undefined && ownerNamePickerDict[index] && ownerNamePickerDict[index].current) {
            ownerNamePickerDict[index].current.input.current._updateValue("");
          }
          break;
        case "ownerEmail":
          if (index !== undefined && ownerEmailPickerDict[index] && ownerEmailPickerDict[index].current) {
            ownerEmailPickerDict[index].current.input.current._updateValue("");
          }
          break;
        default:
          break;
      }
    } else {
      // suggestions logic
      if (filterText && filterText.length >= 2) {
        let result = await getPreviousMeetingParticipants({
          search: filterText,
          customerId,
          type
        });
        if (["sttParticipantEmail", "customerParticipantEmail", "ownerEmail"].includes(type)) {
          filterText = filterText.toLowerCase();
        }
        if (result?.success && result.data.length > 0) {
          let names: ITag[] = [];
          result?.data.forEach((item: any) => {
            const list = item[type]?.split(",");
            list?.forEach((d: string) => {
              if (d.toLowerCase().includes(filterText.toLowerCase())) {
                const found = selectedTagList?.find((dd) => dd.key === d);
                const nameFound = names?.find((dd) => dd.key === d);
                if (!found && !nameFound) {
                  names.push({
                    name: d,
                    key: d,
                  });
                }
              }
            })
          });
          return names;
        } else {
          return [{ key: filterText, name: `Add "${filterText}"` }];
        }
      }
    }
    return [];
  };

  const getFormikErrors = (errors: any, touched: any, index: number, propsName: string, shouldReturnText: boolean) => {
    console.log(errors);
    console.log(touched);
    if (shouldReturnText) return errors && errors.actionItems && errors.actionItems[index] && errors.actionItems[index][propsName];
    let isError = false;
    if (errors && errors.actionItems && errors.actionItems[index] && errors.actionItems[index][propsName] && touched && touched.actionItems && touched.actionItems[index] && touched.actionItems[index][propsName]) {
      isError = true;
    }
    return isError;
  };

  const isNotAdvancedFilter = (meetingDate: Date) => {
    return moment(meetingDate).isSameOrBefore(moment(), "date");
  };

  const handleEmailDialogOpenClose = (isOpen: boolean) => {
    setIsSendEmailDialogOpen(isOpen);
  };

  const exportPreviousMeeting = async (type: 'pdf' | 'excel') => {
    if (type === "pdf") {
      const result = await exportPreviousMeetingPDF(momActionsSearch);
      let pdf = await result.blob();
      const pdfFile = await new Blob([pdf], { type: "application/pdf" });
      const pdfFileURL = await URL.createObjectURL(pdfFile);
      var a: any = document.createElement("a");
      document.body.appendChild(a);
      a.style = "display:none";
      a.href = pdfFileURL;
      a.download = `MOR Customer Meetings List for ${customer || ""}.pdf`;
      // a.download = `MOR Customer Meetings List for ${data?.customerName || ""}.pdf`;
      a.click();
    } else {
      const result = await getMomStatusDetailsExcel({ ...momActionsSearch, meetingIds: [momActionsSearch.meetingId] });
      let xlsx = await result.blob();
      const xlFile = await new Blob([xlsx], {
        type: "application/vnd.openxmlformats",
      });
      const xlFileURL = await URL.createObjectURL(xlFile);
      var a: any = document.createElement("a");
      document.body.appendChild(a);
      a.style = "display:none";
      a.href = xlFileURL;
      a.download = `MOR Customer Meetings List for ${customer || ""}.xlsx`;
      // a.download = `MOR Customer Meetings List for ${data?.customerName || ""}.xlsx`;
      a.click();
    }
  };

  const startOfADay = (date: Date) => {
    return moment(date).startOf("day").toDate();
  };

  const checkEditAccess = (userId: string) => {
    return ((tokenData.isAdmin && tokenData.role === 0) || userId === tokenData._id);
  };

  const onSort = (key: typeof momActionsSearch.sortBy, values: IPreviousMeeting) => {
    let sortBy = momActionsSearch.sortBy;
    let sortOrder = momActionsSearch.sortOrder;
    if (key === sortBy) {
      sortOrder = sortOrder === "asc" ? "desc" : "asc";
    } else {
      sortBy = key;
      sortOrder = "asc";
    }
    values.actionItems = values.actionItems.sort((a: any, b: any) => sortOrder === "asc" ? a[sortBy] > b[sortBy] ? 1 : -1 : b[sortBy] > a[sortBy] ? 1 : -1);
    setState({ ...state, previousMeeting: values });
    setMomActionsSearch({ ...momActionsSearch, sortBy, sortOrder });
  };

  const getMomActionSearchHavingData = (searchObj: any) => {
    const dictWithFilterData = Object.keys(searchObj).reduce((acc: any, d) => {
      const value = searchObj[d];
      if (Array.isArray(value) && value.length > 0) acc[d] = value;
      return acc;
    }, {});
    return dictWithFilterData;
  }

  const onFilter = (searchObj: IPreviousMeetingActionItemsSearch) => {
    let actionItems = data?.actionItems || [];
    const dictWithFilterData = getMomActionSearchHavingData(searchObj);
    const conditionKeys = Object.keys(dictWithFilterData);
    if (conditionKeys.length > 0) {
      actionItems = actionItems.filter((d: any) => {
        let conditionPass = true;
        conditionKeys.forEach((key) => {
          if (key === "ownerName") {
            let ownerNameCP = false;
            const arr = d.ownerName.split(",");
            arr.forEach((ele: string) => {
              if (conditionPass && dictWithFilterData[key].includes(ele)) ownerNameCP = true;
            });
            conditionPass = ownerNameCP;
          } else {
            if (conditionPass && !dictWithFilterData[key].includes(d[key])) conditionPass = false;
          }
        })
        if (conditionPass) return d;
      });
    }
    setMomActionsSearch({ ...searchObj });
    setState({ ...state, previousMeeting: { ...state.previousMeeting, actionItems } });
  };

  const filterSuggestedTagsForActionItems = (filterText: string, tagList: ITag[], key: string): ITag[] => {
    let dict = key === "legendId" ? MEETINGS_LEGEND_DICT : key === "owner" ? MEETINGS_OWNER_DICT : key === "status" ? MEETINGS_STATUS_DICT : data?.actionItems.reduce((acc: any, d) => {
      const ownerNames = d.ownerName.split(",");
      ownerNames.forEach((ownerName) => acc[ownerName] = ownerName);
      return acc
    }, {});
    const tagsArr: ITag[] = Object.keys(dict).map((d) => ({ key: d, name: dict[d] }));

    return filterText
      ? tagsArr.filter(
        (tag) =>
          tag.name.toLowerCase().indexOf(filterText.toLowerCase()) === 0 &&
          !listContainsTagList(tag, tagList)
      )
      : [];
  };

  const showAllTagsForActionItems = (key: string, selectedItems?: ITag[] | undefined) => {
    let dict = key === "legendId" ? MEETINGS_LEGEND_DICT : key === "owner" ? MEETINGS_OWNER_DICT : key === "status" ? MEETINGS_STATUS_DICT : data?.actionItems.reduce((acc: any, d) => {
      const ownerNames = d.ownerName.split(",");
      ownerNames.forEach((ownerName) => acc[ownerName] = ownerName);
      return acc
    }, {});
    const tagsArr: ITag[] = Object.keys(dict).map((d) => ({ key: d, name: dict[d] }));

    return removeDuplicates(tagsArr, selectedItems);
  };

  function removeDuplicates(tagList: ITag[], tags?: ITag[]) {
    return tagList.filter((tag) => !listContainsTagList(tag, tags));
  }

  const listContainsTagList = (tag: ITag, tagList?: ITag[]) => {
    if (!tagList || !tagList.length || tagList.length === 0) {
      return false;
    }
    return tagList.some((compareTag) => compareTag.key === tag.key);
  };

  const onActionItemsEditButtonClick = () => {
    if (Object.keys(getMomActionSearchHavingData(momActionsSearch)).length > 0) {
      showConfirmDialog(
        "Filter Items are Selected",
        "Do you want to remove those and proceed for edit?",
        async () => {
          onFilter({ ...momActionsSearch, legendId: [], owner: [], ownerName: [], status: [] });
          setIsOnEdit(true);
        }
      );
    } else {
      setIsOnEdit(true);
    }
  };

  return (
    <React.Fragment>
      {state.isLoading && <Loading />}
      <CustomDrawer
        title={step === 1
          ? state.previousMeeting._id
            ? "Update Customer Meeting"
            : "Add Customer Meeting"
          : "Customer Meeting Actions"
        }
        handelOpen={true}
        onClose={() => closeAddDialog(step === 2 && !isStepTwoView ? state.previousMeeting : undefined)}
        drawerType={step === 2 ? PanelType.custom : undefined}
        customWidth={step === 2 ? "100%" : undefined}
      >
        <Stack>
          {step === 1 ? (
            <Formik
              initialValues={state.previousMeeting}
              validationSchema={pMSchema}
              onSubmit={async (values: IPreviousMeeting, { setSubmitting }) => {
                let obj = { ...values };
                let result;
                let isAdd = false;
                setState({ ...state, isLoading: true });
                if (state.previousMeeting._id) {
                  result = await updatePreviousMeeting(obj);
                } else {
                  result = await addPreviousMeeting(obj);
                  isAdd = true;
                }
                if (result.success) {
                  showToast(
                    `Customer meeting ${obj._id ? "updated" : "added"
                    } successfully`,
                    "success"
                  );
                  let previousMeeting = { ...state.previousMeeting, ...result.data };
                  // only add a empty action items if it's a add action and meeting date today or before, future meeting date is a advance meeting and may not have a action items
                  if (isAdd && isNotAdvancedFilter(obj.meetingDate)) {
                    previousMeeting = {
                      ...previousMeeting,
                      actionItems: [{
                        discussion: "",
                        legendId: "",
                        owner: "",
                        ownerName: "",
                        ownerEmail: "",
                        startDate: previousMeeting.meetingDate ? startOfADay(previousMeeting.meetingDate) : moment().toDate(),
                        targetDate: previousMeeting.meetingDate ? startOfADay(previousMeeting.meetingDate) : moment().toDate(),
                        completionDate: undefined,
                        status: "open",
                        remarks: "",
                        showInPpt: false,
                      }]
                    };
                  };

                  setState({
                    ...state,
                    previousMeeting,
                    isLoading: false
                  });
                  if (previousMeeting.actionItems.length > 0) {
                    setIsOnEdit(true);
                  }
                  setStep(2);
                } else {
                  setState({ ...state, isLoading: false })
                  showToast(
                    result?.message ||
                    `Error while ${obj._id ? "updating" : "adding"
                    } customer meeting`,
                    "error"
                  );
                }
                setSubmitting(false);
              }}
            >
              {({
                handleSubmit,
                setFieldValue,
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
              }) => (
                <form onSubmit={handleSubmit}>
                  <Stack wrap tokens={childrenGap} className="pt-16">
                    {isFromDashboard && (
                      <Stack.Item className="custom-picker mb-30">
                        <Label>Customers</Label>
                        <NormalPeoplePicker
                          inputProps={{
                            placeholder:
                              "Search customer by name/sap code/email...",
                          }}
                          itemLimit={1}
                          disabled={data?._id ? true : false}
                          onResolveSuggestions={onFilterChanged}
                          removeButtonAriaLabel="Remove"
                          selectedItems={selectedCustomers}
                          pickerSuggestionsProps={pickerSuggestionsProps}
                          onChange={async (items) => {
                            let selectedCustomers: IPersonaProps[] = [];
                            let customerId: string = "";
                            let customerName: string = "";
                            items?.forEach((element) => {
                              selectedCustomers = [element];
                              customerId = element.id as string;
                              customerName = element.text as string;
                            });
                            setFieldValue("customerId", customerId);
                            setSelectedCustomers(selectedCustomers);
                            setCustomer(customerName);
                            if (selectedCustomers.length === 0) setUsersList([]);
                            if (customerId && !isCem) await getCemUser(customerId);
                          }}
                          onRenderSuggestionsItem={onRenderSuggestionsItem}
                        />
                        {errors.customerId && touched.customerId && (
                          <Text className="error-msg">{errors.customerId}</Text>
                        )}
                      </Stack.Item>
                    )}
                    <Stack.Item className="custom-datepicker position-relative mb-30">
                      <label className="ms-Label">Meeting Date</label>
                      <Datetime
                        className="custom-field-group ms-TextField-fieldGroup"
                        inputProps={{
                          className: "ms-TextField-field px-8 border-0 w-100",
                        }}
                        dateFormat="ddd MMM DD YYYY"
                        timeFormat="HH:mm"
                        value={new Date(values.meetingDate)}
                        onChange={(date) => {
                          const value = moment(date).toDate();
                          setFieldValue("meetingDate", value);
                          if (!state.previousMeeting._id) setFieldValue("targetDate", value);
                        }}
                      />
                    </Stack.Item>
                    <Stack.Item className="custom-multiline-textfield mb-30">
                      <TextField
                        name="agenda"
                        value={values.agenda}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        multiline
                        label="Agenda"
                        errorMessage={
                          errors.agenda && touched.agenda
                            ? errors.agenda
                            : ""
                        }
                        autoAdjustHeight
                      />
                    </Stack.Item>
                    <Stack.Item className="custom-picker">
                      <Label>STT Participant's Name</Label>
                      <TagPicker
                        disabled={values.customerId ? false : true}
                        removeButtonAriaLabel="Remove"
                        selectedItems={
                          values.sttParticipantName.split(",").filter((d) => d !== "").map((d) => ({ key: d, name: d }))
                        }
                        onResolveSuggestions={(filter, selectedItems) =>
                          onParticipantNameEmailSearch(filter, 'sttParticipantName', values.customerId, selectedItems, setFieldValue, values.sttParticipantName)
                        }
                        onChange={(items) => {
                          let sttParticipantNames: string[] = [];
                          items?.forEach((i) => {
                            sttParticipantNames.push(i.key as string);
                          });
                          setFieldValue("sttParticipantName", sttParticipantNames.join(","));
                          console.log(values);
                        }}
                        componentRef={sttParticipantNamePicker}
                      />
                      {isFromDashboard && !values.customerId && <div><small>Select customer first to enable this field</small></div>}
                      {errors.sttParticipantName && touched.sttParticipantName && (
                        <Text className="error-msg">
                          {errors.sttParticipantName}
                        </Text>
                      )}
                    </Stack.Item>
                    <Stack.Item className="custom-picker">
                      <Label>STT Participant's Email</Label>
                      <TagPicker
                        disabled={values.customerId ? false : true}
                        removeButtonAriaLabel="Remove"
                        selectedItems={
                          values.sttParticipantEmail.split(",").filter((d) => d !== "").map((d) => ({ key: d, name: d }))
                        }
                        onResolveSuggestions={(filter, selectedItems) =>
                          onParticipantNameEmailSearch(filter, 'sttParticipantEmail', values.customerId, selectedItems, setFieldValue, values.sttParticipantEmail)
                        }
                        onChange={(items) => {
                          let sttParticipantNames: string[] = [];
                          items?.forEach((i) => {
                            sttParticipantNames.push(i.key as string);
                          });
                          setFieldValue("sttParticipantEmail", sttParticipantNames.join(","));
                          console.log(values);
                        }}
                        componentRef={sttParticipantEmailPicker}
                      />
                      {isFromDashboard && !values.customerId && <div><small>Select customer first to enable this field</small></div>}
                      {errors.sttParticipantEmail && touched.sttParticipantEmail && (
                        <Text className="error-msg">
                          {errors.sttParticipantEmail}
                        </Text>
                      )}
                    </Stack.Item>
                    <Stack.Item className="custom-picker">
                      <Label>Customer Participant's Name</Label>
                      <TagPicker
                        disabled={values.customerId ? false : true}
                        removeButtonAriaLabel="Remove"
                        selectedItems={
                          values.customerParticipantName.split(",").filter((d) => d !== "").map((d) => ({ key: d, name: d }))
                        }
                        onResolveSuggestions={(filter, selectedItems) =>
                          onParticipantNameEmailSearch(filter, 'customerParticipantName', values.customerId, selectedItems, setFieldValue, values.customerParticipantName)
                        }
                        onChange={(items) => {
                          let customerParticipantNames: string[] = [];
                          items?.forEach((i) => {
                            customerParticipantNames.push(i.key as string);
                          });
                          setFieldValue("customerParticipantName", customerParticipantNames.join(","));
                          console.log(values);
                        }}
                        componentRef={customerParticipantNamePicker}
                      />
                      {isFromDashboard && !values.customerId && <div><small>Select customer first to enable this field</small></div>}
                      {errors.customerParticipantName && touched.customerParticipantName && (
                        <Text className="error-msg">
                          {errors.customerParticipantName}
                        </Text>
                      )}
                    </Stack.Item>
                    <Stack.Item className="custom-picker">
                      <Label>Customer Participant's Email</Label>
                      <TagPicker
                        disabled={values.customerId ? false : true}
                        removeButtonAriaLabel="Remove"
                        selectedItems={
                          values.customerParticipantEmail.split(",").filter((d) => d !== "").map((d) => ({ key: d, name: d }))
                        }
                        onResolveSuggestions={(filter, selectedItems) =>
                          onParticipantNameEmailSearch(filter, 'customerParticipantEmail', values.customerId, selectedItems, setFieldValue, values.customerParticipantEmail)
                        }
                        onChange={(items) => {
                          let customerParticipantNames: string[] = [];
                          items?.forEach((i) => {
                            customerParticipantNames.push(i.key as string);
                          });
                          setFieldValue("customerParticipantEmail", customerParticipantNames.join(","));
                          console.log(values);
                        }}
                        componentRef={customerParticipantEmailPicker}
                      />
                      {isFromDashboard && !values.customerId && <div><small>Select customer first to enable this field</small></div>}
                      {errors.customerParticipantEmail && touched.customerParticipantEmail && (
                        <Text className="error-msg">
                          {errors.customerParticipantEmail}
                        </Text>
                      )}
                    </Stack.Item>
                    <Stack.Item>
                      <Stack.Item className="custom-dropdown">
                        <Dropdown
                          options={Object.keys(MEETINGS_LOCATION_DICT).map((d) => ({ key: d, text: MEETINGS_LOCATION_DICT[d] }))}
                          selectedKey={values.meetingLocation}
                          onChange={(e, item) => {
                            setFieldValue("meetingLocation", item?.key);
                          }}
                          label="Meeting Location"
                          placeholder="Select an option"
                          errorMessage={
                            (errors.meetingLocation &&
                              touched.meetingLocation &&
                              errors.meetingLocation) as string
                          }
                        />
                      </Stack.Item>
                    </Stack.Item>
                    <Stack.Item className="custom-dropdown mb-30 pt-24">
                      <Dropdown
                        options={isCem ? [{ key: tokenData._id, text: tokenData.name }] : usersList}
                        // options={isCem ? usersList.filter((d) => d.key === values.userId) : usersList}
                        selectedKey={values.userId}
                        onChange={(e, item) => {
                          setFieldValue("userId", item?.key);
                        }}
                        label="Added By"
                        placeholder="Select an option"
                        errorMessage={
                          (errors.userId &&
                            touched.userId &&
                            errors.userId) as string
                        }
                      />
                    </Stack.Item>
                    <Stack.Item>
                      <Stack
                        horizontal
                        wrap
                        tokens={childrenGap}
                        horizontalAlign="end"
                        className="pt-16"
                      >
                        <DefaultButton
                          text="Cancel"
                          onClick={() => closeAddDialog()}
                          className="borderless-btn"
                        />
                        <PrimaryButton
                          type="submit"
                          text={state.previousMeeting._id ? "Update" : "Add"}
                        />
                      </Stack>
                    </Stack.Item>
                  </Stack>
                </form>
              )}
            </Formik>
          ) : (
            <Formik
              enableReinitialize
              initialValues={state.previousMeeting}
              validationSchema={pMSchema}
              onSubmit={async (values: IPreviousMeeting, { setSubmitting }) => {
                let obj = { ...values };
                obj.actionItems = obj.actionItems.map((d) => {
                  d.startDate = d.startDate ? startOfADay(d.startDate) : d.startDate;
                  d.targetDate = d.targetDate ? startOfADay(d.targetDate) : d.targetDate;
                  d.completionDate = d.completionDate ? startOfADay(d.completionDate) : d.completionDate;
                  if (d.showInPpt && d.status !== "closed") d.showInPpt = false; // only applicable for status closed
                  return d;
                })
                setState({ ...state, isLoading: true });
                const result = await updatePreviousMeeting(obj);
                handleLoading();
                if (result.success) {
                  setState({ ...state, isLoading: false })
                  showToast(
                    `Customer meeting actions added/updated successfully`,
                    "success"
                  );
                  closeAddDialog(result.data);
                } else {
                  setState({ ...state, isLoading: false });
                  showToast(
                    result?.message ||
                    `Error while adding/updating customer meeting actions`,
                    "error"
                  );
                }
                setSubmitting(false);
              }}
            >
              {({
                handleSubmit,
                setFieldValue,
                values,
                errors,
                touched,
                // handleChange,
                // handleBlur,
              }) => (
                <form onSubmit={handleSubmit}>
                  <Stack wrap tokens={childrenGap} className="pt-16">
                    <Stack.Item>
                      <FieldArray name="actionItems">
                        {({ insert, remove }) => (
                          <Stack tokens={childrenGap}>
                            {isStepTwoView && (data && data.actionItems?.length > 0) && (
                              <Stack.Item>
                                <Stack horizontal tokens={childrenGap} wrap>
                                  <Stack.Item>
                                    <TagPicker
                                      selectedItems={momActionsSearch.legendId?.map((d) => ({ key: d, name: MEETINGS_LEGEND_DICT[d] }))}
                                      removeButtonAriaLabel="Remove"
                                      onResolveSuggestions={(filter, selectedItems) => filterSuggestedTagsForActionItems(filter, selectedItems as ITag[], "legendId")}
                                      pickerSuggestionsProps={{
                                        noResultsFoundText: "No legend found",
                                      }}
                                      onEmptyResolveSuggestions={(selectedItems) => showAllTagsForActionItems("legendId", selectedItems)}
                                      inputProps={{
                                        placeholder: "Select a legend",
                                      }}
                                      styles={{ root: { minWidth: "150px" } }}
                                      onChange={(items: any) => {
                                        const legendId: string[] = [];
                                        items.forEach((i: any) => {
                                          legendId.push(i.key);
                                        });
                                        onFilter({ ...momActionsSearch, legendId });
                                      }}
                                      disabled={isOnEdit}
                                    />
                                  </Stack.Item>
                                  <Stack.Item>
                                    <TagPicker
                                      selectedItems={momActionsSearch.owner?.map((d) => ({ key: d, name: MEETINGS_OWNER_DICT[d] }))}
                                      removeButtonAriaLabel="Remove"
                                      onResolveSuggestions={(filter, selectedItems) => filterSuggestedTagsForActionItems(filter, selectedItems as ITag[], "owner")}
                                      pickerSuggestionsProps={{
                                        noResultsFoundText: "No owner found",
                                      }}
                                      onEmptyResolveSuggestions={(selectedItems) => showAllTagsForActionItems("owner", selectedItems)}
                                      inputProps={{
                                        placeholder: "Select a owner",
                                      }}
                                      styles={{ root: { minWidth: "150px" } }}
                                      onChange={(items: any) => {
                                        const owner: string[] = [];
                                        items.forEach((i: any) => {
                                          owner.push(i.key);
                                        });
                                        onFilter({ ...momActionsSearch, owner });
                                      }}
                                      disabled={isOnEdit}
                                    />
                                  </Stack.Item>
                                  <Stack.Item>
                                    <TagPicker
                                      selectedItems={momActionsSearch.ownerName?.map((d) => ({ key: d, name: d }))}
                                      removeButtonAriaLabel="Remove"
                                      onResolveSuggestions={(filter, selectedItems) => filterSuggestedTagsForActionItems(filter, selectedItems as ITag[], "ownerName")}
                                      pickerSuggestionsProps={{
                                        noResultsFoundText: "No owner name found",
                                      }}
                                      onEmptyResolveSuggestions={(selectedItems) => showAllTagsForActionItems("ownerName", selectedItems)}
                                      inputProps={{
                                        placeholder: "Select a owner name",
                                      }}
                                      styles={{ root: { minWidth: "150px" } }}
                                      onChange={(items: any) => {
                                        const ownerName: string[] = [];
                                        items.forEach((i: any) => {
                                          ownerName.push(i.key);
                                        });
                                        onFilter({ ...momActionsSearch, ownerName });
                                      }}
                                      disabled={isOnEdit}
                                    />
                                  </Stack.Item>
                                  <Stack.Item>
                                    <TagPicker
                                      selectedItems={momActionsSearch.status?.map((d) => ({ key: d, name: MEETINGS_STATUS_DICT[d] }))}
                                      removeButtonAriaLabel="Remove"
                                      onResolveSuggestions={(filter, selectedItems) => filterSuggestedTagsForActionItems(filter, selectedItems as ITag[], "status")}
                                      pickerSuggestionsProps={{
                                        noResultsFoundText: "No status found",
                                      }}
                                      onEmptyResolveSuggestions={(selectedItems) => showAllTagsForActionItems("status", selectedItems)}
                                      inputProps={{
                                        placeholder: "Select a status",
                                      }}
                                      styles={{ root: { minWidth: "150px" } }}
                                      onChange={(items: any) => {
                                        const status: string[] = [];
                                        items.forEach((i: any) => {
                                          status.push(i.key);
                                        });
                                        onFilter({ ...momActionsSearch, status });
                                      }}
                                      disabled={isOnEdit}
                                    />
                                  </Stack.Item>
                                </Stack>
                              </Stack.Item>
                            )}
                            <Stack.Item>
                              <Stack horizontal tokens={childrenGap} wrap horizontalAlign="space-between" verticalAlign="center">
                                <Stack.Item>
                                  <Stack horizontal tokens={childrenGap8}>
                                    <Stack.Item>
                                      <Text className="mom-customer-label">Customer:</Text>
                                    </Stack.Item>
                                    <Stack.Item>
                                      <Text className="mom-customer-name">{customer}</Text>
                                    </Stack.Item>
                                  </Stack>
                                </Stack.Item>
                                <Stack.Item>
                                  <Stack horizontal horizontalAlign="end" tokens={childrenGap}>
                                    {isUpdateAction && !isOnEdit && (
                                      <>
                                        <Stack.Item>
                                          <PrimaryButton
                                            text="Export"
                                            split
                                            splitButtonAriaLabel="See 2 options"
                                            aria-roledescription="split button"
                                            // iconProps={{ color: "white" }}
                                            disabled={values.actionItems.length === 0}
                                            menuProps={{
                                              items: [
                                                {
                                                  key: "asExcel",
                                                  text: "As Excel",
                                                  onClick: () => { exportPreviousMeeting("excel") },
                                                },
                                                {
                                                  key: "asPdf",
                                                  text: "As PDF",
                                                  onClick: () => { exportPreviousMeeting("pdf") },
                                                },
                                              ],
                                            }}
                                          />
                                        </Stack.Item>
                                        {checkEditAccess(values.userId) && (
                                          <Stack.Item>
                                            <PrimaryButton text="Send Email" onClick={() => handleEmailDialogOpenClose(true)} disabled={values.actionItems.length === 0} />
                                          </Stack.Item>
                                        )}
                                      </>
                                    )}
                                    {isStepTwoView && isOnEdit && (
                                      <Stack.Item>
                                        <DefaultButton text="Cancel" className="borderless-btn" onClick={() => {
                                          setFieldValue("actionItems", state.previousMeeting.actionItems);
                                          // setState({ ...state, previousMeeting: { ...state.previousMeeting, ...data } });
                                          setIsOnEdit(false);
                                        }} />
                                      </Stack.Item>
                                    )}
                                    {checkEditAccess(values.userId) && isStepTwoView && !isOnEdit && (
                                      <Stack.Item>
                                        <PrimaryButton text="Edit" onClick={() => onActionItemsEditButtonClick()} disabled={values.actionItems.length === 0} />
                                      </Stack.Item>
                                    )}
                                    {checkEditAccess(values.userId) && (isOnEdit || !data?.actionItems?.length) && (
                                      <Stack.Item>
                                        <DefaultButton
                                          text="Add A Row"
                                          onClick={() => {
                                            setIsOnEdit(true);
                                            insert(0, {
                                              discussion: "",
                                              legendId: "",
                                              owner: "",
                                              ownerName: "",
                                              ownerEmail: "",
                                              startDate: values.meetingDate ? startOfADay(values.meetingDate) : moment().toDate(),
                                              targetDate: values.meetingDate ? startOfADay(values.meetingDate) : moment().toDate(),
                                              completionDate: undefined,
                                              status: "open",
                                              remarks: "",
                                              showInPpt: false,
                                            });
                                          }}
                                        />
                                      </Stack.Item>
                                    )}
                                  </Stack>
                                </Stack.Item>
                              </Stack>
                            </Stack.Item>
                            <Stack.Item>
                              <div className="office-table bg-white mom-action-items-table">
                                <table>
                                  <thead className="html-table-thead">
                                    <tr style={{ height: 44 }}>
                                      <th style={{ minWidth: 10 }}>Sr. No</th>
                                      <th style={{ minWidth: 250 }}>Discussion</th>
                                      <th style={{ minWidth: 90 }}>Legend</th>
                                      <th style={{ minWidth: 90 }}>Owner</th>
                                      <th style={{ minWidth: 150 }}>Owner Name</th>
                                      <th style={{ minWidth: 150 }}>Owner Email</th>
                                      <SortedTableHeader value="startDate" label="Start Date" sortBy={momActionsSearch.sortBy} sortOrder={momActionsSearch.sortOrder} minWidth={130} onSort={(key: typeof momActionsSearch.sortBy) => onSort(key, values)} />
                                      <SortedTableHeader value="targetDate" label="Target Date" sortBy={momActionsSearch.sortBy} sortOrder={momActionsSearch.sortOrder} minWidth={130} onSort={(key: typeof momActionsSearch.sortBy) => onSort(key, values)} />
                                      <SortedTableHeader value="completionDate" label="Close Date" sortBy={momActionsSearch.sortBy} sortOrder={momActionsSearch.sortOrder} minWidth={130} onSort={(key: typeof momActionsSearch.sortBy) => onSort(key, values)} />
                                      {/* <th style={{ minWidth: 130 }}>Start Date</th>
                                      <th style={{ minWidth: 130 }}>Target Date</th>
                                      <th style={{ minWidth: 130 }}>Close Date</th> */}
                                      {/* <th style={{ minWidth: 130 }}>Actual Date</th> */}
                                      <SortedTableHeader value="status" label="Status" sortBy={momActionsSearch.sortBy} sortOrder={momActionsSearch.sortOrder} minWidth={120} onSort={(key: typeof momActionsSearch.sortBy) => onSort(key, values)} />
                                      {/* <th style={{ minWidth: 120 }}>Status</th> */}
                                      <th style={{ minWidth: 200 }}>Remarks</th>
                                      {!isOnEdit && isUpdateAction && <SortedTableHeader value="aging" label="Aging (in Days)" sortBy={momActionsSearch.sortBy} sortOrder={momActionsSearch.sortOrder} minWidth={124} onSort={(key: typeof momActionsSearch.sortBy) => onSort(key, values)} />}
                                      {/* {!isOnEdit && isUpdateAction && <th style={{ minWidth: 100 }}>Aging (in Days)</th>} */}
                                      <th style={{ minWidth: 50 }}>Show In PPT</th>
                                      {isOnEdit && ((isNotAdvancedFilter(values.meetingDate) && values.actionItems.length > 1) || (!isNotAdvancedFilter(values.meetingDate) && values.actionItems.length > 0)) && <th style={{ minWidth: 50 }}>Actions</th>}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {values.actionItems.length > 0 ? (
                                      values.actionItems.map((d, index) => (
                                        <tr key={index} style={{ verticalAlign: "baseline" }}>
                                          {isOnEdit ? (
                                            <>
                                              <td align="center">{index + 1}.</td>
                                              <td>
                                                <CustomTextField
                                                  name={`actionItems[${index}].discussion`}
                                                  multiline
                                                  autoAdjustHeight
                                                />
                                              </td>
                                              <td>
                                                <Dropdown
                                                  placeholder="Select a legend"
                                                  selectedKey={d.legendId}
                                                  options={Object.keys(MEETINGS_LEGEND_DICT).map((d) => ({ key: d, text: MEETINGS_LEGEND_DICT[d] }))}
                                                  onChange={(e, v) => {
                                                    setFieldValue(`actionItems[${index}].legendId`, v?.key)
                                                  }}
                                                />
                                                {getFormikErrors(errors, touched, index, "legendId", false) && (
                                                  <Text className="error-msg">
                                                    {getFormikErrors(errors, touched, index, "legendId", true)}
                                                  </Text>
                                                )}
                                              </td>
                                              <td>
                                                <Dropdown
                                                  placeholder="Select a owner"
                                                  selectedKey={d.owner}
                                                  options={Object.keys(MEETINGS_OWNER_DICT).map((d) => ({ key: d, text: MEETINGS_OWNER_DICT[d] }))}
                                                  onChange={(e, v) => {
                                                    setFieldValue(`actionItems[${index}].owner`, v?.key)
                                                  }}
                                                />
                                                {getFormikErrors(errors, touched, index, "owner", false) && (
                                                  <Text className="error-msg">
                                                    {getFormikErrors(errors, touched, index, "owner", true)}
                                                  </Text>
                                                )}
                                              </td>
                                              <td>
                                                <TagPicker
                                                  removeButtonAriaLabel="Remove"
                                                  selectedItems={
                                                    d.ownerName.split(",").filter((d) => d !== "").map((d) => ({ key: d, name: d }))
                                                  }
                                                  onResolveSuggestions={(filter, selectedItems) =>
                                                    onParticipantNameEmailSearch(filter, 'ownerName', values.customerId, selectedItems, setFieldValue, d.ownerName, index)
                                                  }
                                                  onChange={(items) => {
                                                    let ownerNames: string[] = [];
                                                    items?.forEach((i) => {
                                                      ownerNames.push(i.key as string);
                                                    });
                                                    setFieldValue(`actionItems[${index}].ownerName`, ownerNames.join(","));
                                                  }}
                                                  componentRef={ownerNamePickerDict[index]}
                                                />
                                                {getFormikErrors(errors, touched, index, "ownerName", false) && (
                                                  <Text className="error-msg">
                                                    {getFormikErrors(errors, touched, index, "ownerName", true)}
                                                  </Text>
                                                )}
                                              </td>
                                              <td>
                                                <TagPicker
                                                  removeButtonAriaLabel="Remove"
                                                  selectedItems={
                                                    d.ownerEmail.split(",").filter((d) => d !== "").map((d) => ({ key: d, name: d }))
                                                  }
                                                  onResolveSuggestions={(filter, selectedItems) =>
                                                    onParticipantNameEmailSearch(filter, 'ownerEmail', values.customerId, selectedItems, setFieldValue, d.ownerEmail, index)
                                                  }
                                                  onChange={(items) => {
                                                    let ownerEmails: string[] = [];
                                                    items?.forEach((i) => {
                                                      ownerEmails.push(i.key as string);
                                                    });
                                                    setFieldValue(`actionItems[${index}].ownerEmail`, ownerEmails.join(","));
                                                  }}
                                                  componentRef={ownerEmailPickerDict[index]}
                                                />
                                                {getFormikErrors(errors, touched, index, "ownerEmail", false) && (
                                                  <Text className="error-msg">
                                                    {getFormikErrors(errors, touched, index, "ownerEmail", true)}
                                                  </Text>
                                                )}
                                              </td>
                                              <td>
                                                <DatePicker
                                                  firstDayOfWeek={DayOfWeek.Sunday}
                                                  value={new Date(d.startDate)}
                                                  showGoToToday={false}
                                                  showMonthPickerAsOverlay={true}
                                                  onSelectDate={(date: Date | null | undefined) =>
                                                    setFieldValue(`actionItems[${index}].startDate`, date)
                                                  }
                                                />
                                              </td>
                                              <td>
                                                <DatePicker
                                                  firstDayOfWeek={DayOfWeek.Sunday}
                                                  value={new Date(d.targetDate)}
                                                  showGoToToday={false}
                                                  showMonthPickerAsOverlay={true}
                                                  onSelectDate={(date: Date | null | undefined) =>
                                                    setFieldValue(`actionItems[${index}].targetDate`, date)
                                                  }
                                                />
                                              </td>
                                              <td>
                                                <DatePicker
                                                  firstDayOfWeek={DayOfWeek.Sunday}
                                                  value={d.completionDate ? new Date(d.completionDate) : undefined}
                                                  showGoToToday={false}
                                                  showMonthPickerAsOverlay={true}
                                                  onSelectDate={(date: Date | null | undefined) =>
                                                    setFieldValue(`actionItems[${index}].completionDate`, date)
                                                  }
                                                  placeholder="Select date"
                                                />
                                                {getFormikErrors(errors, touched, index, "completionDate", true) && (
                                                  <Text className="error-msg">
                                                    {getFormikErrors(errors, touched, index, "completionDate", true)}
                                                  </Text>
                                                )}
                                              </td>
                                              <td>
                                                <Dropdown
                                                  selectedKey={d.status}
                                                  options={Object.keys(MEETINGS_STATUS_DICT).map((d) => ({ key: d, text: MEETINGS_STATUS_DICT[d] }))}
                                                  onChange={(e, v) => {
                                                    setFieldValue(`actionItems[${index}].status`, v?.key)
                                                  }}
                                                />
                                              </td>
                                              <td>
                                                <CustomTextField
                                                  name={`actionItems[${index}].remarks`}
                                                  multiline
                                                  autoAdjustHeight
                                                />
                                              </td>
                                              {!isOnEdit && isUpdateAction && <td>{d.aging == null ? "NA" : d.aging}</td>}
                                              {d.status === "closed" ? (
                                                <td align="center">
                                                  <Toggle
                                                    checked={d.showInPpt}
                                                    // onText="Yes"
                                                    // offText="No"
                                                    onChange={(e, v) => {
                                                      setFieldValue(`actionItems[${index}].showInPpt`, v);
                                                    }}
                                                  />
                                                </td>
                                              ) : <td align="center">NA</td>}
                                              {isOnEdit && ((isNotAdvancedFilter(values.meetingDate) && values.actionItems.length > 1) || (!isNotAdvancedFilter(values.meetingDate))) && (
                                                <td align="center">
                                                  <TooltipHost
                                                    content="Remove"
                                                    calloutProps={{
                                                      gapSpace: 0,
                                                    }}
                                                  >
                                                    <span
                                                      onClick={() => {
                                                        remove(index);
                                                      }}
                                                    >
                                                      <Icon
                                                        path={mdiTrashCan}
                                                        size={0.9}
                                                        className="pointer"
                                                        color="red"
                                                      />
                                                    </span>
                                                  </TooltipHost>
                                                </td>
                                              )}
                                            </>
                                          ) : (
                                            <>
                                              <td align="center">{index + 1}.</td>
                                              <td>{d.discussion}</td>
                                              <td>{MEETINGS_LEGEND_DICT[d.legendId]}</td>
                                              <td>{MEETINGS_OWNER_DICT[d.owner]}</td>
                                              <td>{d.ownerName}</td>
                                              <td>{d.ownerEmail}</td>
                                              <td>{moment(d.startDate).format("DD/MM/YYYY")}</td>
                                              <td>{moment(d.targetDate).format("DD/MM/YYYY")}</td>
                                              <td>{d.completionDate ? moment(d.completionDate).format("DD/MM/YYYY") : "NA"}</td>
                                              <td>{MEETINGS_STATUS_DICT[d.status]}</td>
                                              <td>{d.remarks}</td>
                                              {!isOnEdit && isUpdateAction && <td align="right">{d.aging == null ? "NA" : d.aging}</td>}
                                              <td>{d.status === "closed" ? d.showInPpt ? "Yes" : "No" : "NA"}</td>
                                            </>
                                          )}
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan={isUpdateAction ? 13 : 12} align="center">{`${Object.keys(getMomActionSearchHavingData(momActionsSearch)).length > 0 ? "No action items found" : data ? 'No action items found. If you want to add it, please click on "Add A Row" button.' : 'No action items added as meeting date is future date. If you want to add it, please click on "Add A Row" button.'}`}</td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </Stack.Item>
                          </Stack>
                        )}
                      </FieldArray>
                    </Stack.Item>
                    {checkEditAccess(values.userId) && (
                      <Stack.Item>
                        <Stack
                          horizontal
                          wrap
                          tokens={childrenGap}
                          horizontalAlign="end"
                          className="pt-16"
                        >
                          {/* <DefaultButton
                          text="Close"
                          onClick={() => closeAddDialog()}
                          className="borderless-btn"
                        /> */}
                          <PrimaryButton
                            type="submit"
                            text="Save"
                            disabled={isStepTwoView && !isOnEdit}
                          />
                        </Stack>
                      </Stack.Item>
                    )}
                  </Stack>
                </form>
              )}
            </Formik>
          )}
        </Stack>
      </CustomDrawer>
      {isSendEmailDialogOpen && (
        <ManagePmSendEmailDialog
          meetingIds={[data?._id as string]}
          handleClose={() =>
            handleEmailDialogOpenClose(false)
          }
          meetingList={[data as IPreviousMeeting]}
        />
      )}
    </React.Fragment>
  );
};

interface SortedTableHeaderProps {
  sortBy: string;
  sortOrder: string;
  label: string;
  value: string;
  onSort: Function;
  minWidth: number;
}

const SortedTableHeader: React.FC<SortedTableHeaderProps> = ({ sortBy, sortOrder, label, value, onSort, minWidth }) => {
  return (
    <th style={{ minWidth, cursor: "pointer" }} onClick={() => onSort(value)}>
      <Stack horizontal verticalAlign="center" horizontalAlign="center" tokens={{ childrenGap: 4 }}>
        <Stack.Item>
          <Text className="mom-action-th">{label}</Text>
        </Stack.Item>
        {value === sortBy && (
          <Stack.Item>
            <Icon
              path={sortOrder === "asc" ? mdiArrowUp : mdiArrowDown}
              size={0.9}
              color="white"
            />
          </Stack.Item>
        )}
      </Stack>
    </th>
  );
}

export default ManagePmDialog;
